import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Modal,
  Typography,
  LinearProgress,
  Stack,
  IconButton,
  Fade,
} from '@mui/material';
import {Close as CloseIcon} from '@mui/icons-material';

/**
 * PrintPreparationModal
 *
 * Shows an indeterminate progress indicator while the document prepares for
 * print or download. For Word export, switches to a determinate progress bar
 * tracking per-table screenshot generation.
 */
const PrintPreparationModal = ({
  open,
  onCancel = null,
  isDownloadingToWord = false,
  // Word export specific props
  isWordExportPhase = false,
  wordExportProgress = 0,
}) => {
  // Blur the currently focused element when modal opens to prevent
  // aria-hidden warning. MUI's Modal adds aria-hidden to the root element,
  // but if a button in the background retains focus, the browser warns.
  useEffect(() => {
    if (open && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [open]);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 450,
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    outline: 'none',
  };

  return (
    <Modal
      open={open}
      onClose={null} // Prevent closing by clicking outside
      closeAfterTransition
      sx={{
        backdropFilter: 'blur(3px)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
    >
      <Fade in={open}>
        <Box sx={modalStyle}>
          <Stack spacing={3}>
            {/* Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" component="h2" fontWeight={600}>
                {isDownloadingToWord
                  ? 'Preparing for Download'
                  : 'Preparing for Print'}
              </Typography>
              {onCancel && (
                <IconButton
                  onClick={onCancel}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {color: 'error.main'},
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Status Message */}
            <Box>
              <Typography variant="body2" color="text.secondary">
                Please wait while we prepare your document.
              </Typography>
            </Box>

            {/* Progress Bar */}
            <Box>
              <LinearProgress
                variant={isWordExportPhase ? 'determinate' : 'indeterminate'}
                value={isWordExportPhase ? wordExportProgress : undefined}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: '#26A993',
                  },
                }}
              />
              {isWordExportPhase && (
                <Box display="flex" justifyContent="flex-start" mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    {wordExportProgress}% complete
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Cancel Warning */}
            {onCancel && (
              <Box
                sx={{borderTop: '1px solid', borderColor: 'grey.200', pt: 2}}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  ⚠️ Canceling now will return you to the document without
                  {isDownloadingToWord ? ' downloading' : ' printing'}.
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};

PrintPreparationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
  isDownloadingToWord: PropTypes.bool,
  // Word export specific props
  isWordExportPhase: PropTypes.bool,
  wordExportProgress: PropTypes.number,
};

export default PrintPreparationModal;
